import { GridColDef, GridFilterModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAllDecomModels,
  getDecomSuspendedUsers,
  UserKeys,
} from '../../../../api/users-api';
import SuspendedUsersGridToolbar from './suspended-users-grid-toolbar';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import PurgeSuspendedUsersDialog from './purge-suspended-users-dialog';
import TerminateSuspendedUsersDialog from './terminate-suspended-users-dialog';
import PauseSuspendedUsersDialog from './pause-suspended-users-dialog';
import UnpauseSuspendedUsersDialog from './unpause-suspended-users-dialog';
import { Messages } from '../../../../utils/message-constants';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { DecomSuspendedUserModel } from '../../../../../types';
import StatusLabel from '../../../../shared/status-label';
import PersonOff from '@mui/icons-material/PersonOff';

export default function SuspendedUsersGrid() {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openPurgeUsers, setOpenPurgeUsers] = useState(false);
  const [openTerminateUsers, setOpenTerminateUsers] = useState(false);
  const [openPauseUsers, setOpenPauseUsers] = useState(false);
  const [openUnpauseUsers, setOpenUnpauseUsers] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const policy = searchParams.get('policy');

  const { changeLogRoles, viewType } = useUserSettings();

  const { data: decomModels } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('decom models'),
    },
  });

  const {
    data: decomSuspendedUsers,
    isFetching: isFetchingDecomSuspendedUsers,
    isLoading: isLoadingDecomSuspendedUsers,
  } = useQuery({
    queryKey: [UserKeys.SuspendedUsers],
    queryFn: () => getDecomSuspendedUsers(),
    staleTime: 0, // Set staleTime to 0 to prevent it from being cached
    meta: {
      errorMessage: Messages.Errors.Network.Loading('suspended users'),
    },
  });

  const columns: GridColDef<DecomSuspendedUserModel>[] = useMemo(
    () => [
      {
        field: 'creationDate',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        display: 'flex',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            return params.toLocaleString();
          }
        },
      },
      {
        field: 'decomModelKeyString',
        headerName: 'Decom Policy',
        display: 'flex',
        flex: 1,
        minWidth: 170,
        type: 'singleSelect',
        valueOptions: decomModels?.map((decomModel) => {
          return {
            label: decomModel.name,
            value: decomModel.id,
          };
        }),
        valueGetter: (value: string) => {
          const model = decomModels?.find((policy) => policy.id === value);
          if (model) {
            return model.id;
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'email',
        headerName: 'User Email',
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'completionTime',
        headerName: 'Completion Time',
        width: 185,
        type: 'dateTime',
        filterable: false,
        display: 'flex',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            return params.toLocaleString();
          }
        },
      },
      {
        field: 'decomComplete',
        headerName: 'Decom Status',
        type: 'boolean',
        width: 140,
        display: 'flex',
      },
      {
        field: 'isTerminated',
        headerName: 'User Status',
        width: 140,
        display: 'flex',
        renderCell: (params) => {
          const status =
            params.value === true
              ? 'TERMINATED'
              : params.row.decomComplete
              ? 'COMPLETED'
              : params.row.errored
              ? 'ERROR'
              : params.value === false && params.row.isactive
              ? 'PROCESSING'
              : params.value === false && !params.row.isactive
              ? 'PAUSED'
              : 'UNKNOWN';
          return status !== 'UNKNOWN' ? <StatusLabel status={status} /> : null;
        },
      },
      {
        field: 'timeSuspended',
        headerName: 'Time Suspended',
        width: 185,
        type: 'dateTime',
        display: 'flex',
        valueGetter: (value: number) => {
          const date = new Date(value);
          if (value === 0) {
            return null;
          } else {
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (params === null) {
            return 'N/A';
          } else {
            return params.toLocaleString();
          }
        },
      },
      {
        field: 'reason',
        headerName: 'Action Needed',
        display: 'flex',
        flex: 2,
        valueFormatter: (params: string | null) => {
          if (params === null) {
            return 'No Action Needed.';
          } else {
            if (params.includes('Error Message')) {
              const errorMessageString = params.split('Error Message')[0];
              return errorMessageString;
            } else {
              return params;
            }
          }
        },
      },
      {
        field: 'userId',
        headerName: 'User ID',
        display: 'flex',
        flex: 1,
        width: 140,
      },
    ],
    [decomModels]
  );

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (filterModel.items.length > 0) {
        for (const obj of filterModel.items) {
          if (
            obj.field === 'decomModelKeyString' &&
            typeof obj.value === 'string'
          ) {
            setSearchParams({ policy: obj.value });
            break;
          } else {
            setSearchParams(undefined);
          }
        }
      } else {
        setSearchParams(undefined);
      }
    },
    [setSearchParams]
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'creationDate',
          sort: 'desc',
        },
      ],
    },
    filter: {
      filterModel: {
        items: [
          {
            id: 'decomModelKeyString',
            field: 'decomModelKeyString',
            operator: 'is',
            value: policy ? policy : '',
          },
        ],
      },
    },
    columns: {
      columnVisibilityModel: {
        userId: false,
        completionTime: false,
        timeSuspended: false,
      },
    },
  };

  const toolbarProps = useMemo(
    () => ({
      setOpenPurgeUsers,
      setOpenTerminateUsers,
      setOpenPauseUsers,
      setOpenUnpauseUsers,
      selectedRows,
      decomSuspendedUsers: decomSuspendedUsers || [],
      decomModels,
    }),
    [selectedRows, decomSuspendedUsers, decomModels]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.decomSuspendedUsersGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(SuspendedUsersGridToolbar, toolbarProps),
          ...createGridOverlays('Suspended Users', <PersonOff />),
        }}
        checkboxSelection
        disableRowSelectionOnClick
        getRowId={(row) => row.userId + row.creationDate}
        rows={decomSuspendedUsers || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        loading={isLoadingDecomSuspendedUsers || isFetchingDecomSuspendedUsers}
        gridStateKey="decomSuspendedUsersGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(row) => {
          navigate({
            pathname: '../logs',
            search: createSearchParams({
              userId: row.row.userId,
              ...(policy ? { policy } : {}),
            }).toString(),
          });
        }}
        density="compact"
        getRowHeight={() => 'auto'}
        onFilterModelChange={onFilterChange}
      />
      {selectedRows.length > 0 && openPurgeUsers ? (
        <PurgeSuspendedUsersDialog
          open={openPurgeUsers}
          handleClose={() => {
            setOpenPurgeUsers(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          userList={decomSuspendedUsers || []}
        />
      ) : null}
      {selectedRows.length > 0 && openTerminateUsers ? (
        <TerminateSuspendedUsersDialog
          open={openTerminateUsers}
          handleClose={() => {
            setOpenTerminateUsers(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          userList={decomSuspendedUsers || []}
        />
      ) : null}
      {selectedRows.length > 0 && openPauseUsers ? (
        <PauseSuspendedUsersDialog
          open={openPauseUsers}
          handleClose={() => {
            setOpenPauseUsers(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          userList={decomSuspendedUsers || []}
        />
      ) : null}
      {selectedRows.length > 0 && openUnpauseUsers ? (
        <UnpauseSuspendedUsersDialog
          open={openUnpauseUsers}
          handleClose={() => {
            setOpenUnpauseUsers(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          userList={decomSuspendedUsers || []}
        />
      ) : null}
    </>
  );
}
